import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogActions,
  Button,
  Field,
  Dropdown,
  Option,
  Checkbox,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  useObligationsByClients,
  useBulkUpdateObligations,
  STATE_TAX_OBLIGATION_OPTIONS,
} from '../../services/stateTaxClientsApi';
import type {
  BulkObligationUpdateResult,
  ObligationTarget,
  StateTaxObligationSummary,
} from '../../types';
import { CalendarDateInput } from '../dates/CalendarDateInput';
import { cadenceName } from '../../utils/cadenceName';

/**
 * Obligations mode of the "edit multiple" dialog. Loads every obligation belonging to the
 * selected clients, lets the staffer filter (tax type / state / status) to narrow, fine-tune the
 * resulting checkbox list, then set new values for frequency (+ effective date) / information
 * source / active / has-direct-access and apply across the chosen obligations. Per-obligation
 * success/failure is reported. Renders its own DialogContent + DialogActions so the parent
 * dialog stays a thin mode-switching shell.
 */
const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  filterRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: tokens.spacingHorizontalS,
  },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalM },
  list: {
    maxHeight: '240px',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  listRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    padding: '2px 0',
  },
  rowMeta: { color: tokens.colorNeutralForeground3 },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  countHint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  linkBtns: { display: 'flex', gap: tokens.spacingHorizontalS },
  sectionLabel: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300 },
});

const UNCHANGED = ''; // dropdown sentinel — "leave this field alone"
const ANY = '__any__'; // filter sentinel — "no filter on this dimension"

type ActiveChoice = 'unchanged' | 'active' | 'inactive';
type AccessChoice = 'unchanged' | 'yes' | 'no';

interface FlatObligation {
  clientId: string;
  clientName: string;
  obligation: StateTaxObligationSummary;
}

interface Props {
  selectedClients: { id: string; name: string }[];
  onOpenChange: (open: boolean) => void;
  onCommitted?: (result: BulkObligationUpdateResult) => void;
}

export default function BulkEditObligationsContent({ selectedClients, onOpenChange, onCommitted }: Props) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  const clientIds = useMemo(() => selectedClients.map((c) => c.id), [selectedClients]);
  const nameById = useMemo(
    () => new Map(selectedClients.map((c) => [c.id, c.name])),
    [selectedClients],
  );
  const { data: byClient, isLoading, isError } = useObligationsByClients(clientIds);
  const bulkMutation = useBulkUpdateObligations();

  // Flatten { clientId -> obligations[] } into one list for filtering + selection.
  const flat = useMemo<FlatObligation[]>(() => {
    if (!byClient) return [];
    return Object.entries(byClient).flatMap(([cid, obs]) =>
      obs.map((o) => ({ clientId: cid, clientName: nameById.get(cid) ?? cid, obligation: o })),
    );
  }, [byClient, nameById]);

  // Filters.
  const [filterType, setFilterType] = useState<string>(ANY);
  const [filterState, setFilterState] = useState<string>(ANY);
  const [filterStatus, setFilterStatus] = useState<'any' | 'active' | 'inactive'>('active');

  const filtered = useMemo(() => {
    return flat.filter(({ obligation: o }) => {
      if (filterType !== ANY && o.taxType !== filterType) return false;
      if (filterState !== ANY && o.stateLabel !== filterState) return false;
      if (filterStatus === 'active' && !o.active) return false;
      if (filterStatus === 'inactive' && o.active) return false;
      return true;
    });
  }, [flat, filterType, filterState, filterStatus]);

  // Selection — a set of obligation ids. Re-seeded to "all filtered" whenever the filter changes.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelectedIds(new Set(filtered.map((f) => f.obligation.id)));
  }, [filtered]);

  // Field changes.
  const [frequency, setFrequency] = useState<string>(UNCHANGED);
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [informationSource, setInformationSource] = useState<string>(UNCHANGED);
  const [activeChoice, setActiveChoice] = useState<ActiveChoice>('unchanged');
  const [accessChoice, setAccessChoice] = useState<AccessChoice>('unchanged');

  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkObligationUpdateResult | null>(null);

  const hasFrequency = frequency !== UNCHANGED;
  const hasInfoSource = informationSource !== UNCHANGED;
  const hasActive = activeChoice !== 'unchanged';
  const hasAccess = accessChoice !== 'unchanged';
  const isBusy = bulkMutation.isPending;

  const toggleId = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  function validate(): boolean {
    if (selectedIds.size === 0) {
      setFormError(t('bulkEdit.obligations.selectOne'));
      return false;
    }
    if (!hasFrequency && !hasInfoSource && !hasActive && !hasAccess) {
      setFormError(t('bulkEdit.validation.noField'));
      return false;
    }
    if (hasFrequency && !effectiveDate) {
      setFormError(t('bulkEdit.validation.effectiveDateRequired'));
      return false;
    }
    setFormError(null);
    return true;
  }

  async function apply() {
    if (!validate()) return;
    const targets: ObligationTarget[] = filtered
      .filter((f) => selectedIds.has(f.obligation.id))
      .map((f) => ({ clientId: f.clientId, obligationId: f.obligation.id, taxType: f.obligation.taxType }));
    try {
      const res = await bulkMutation.mutateAsync({
        targets,
        frequency: hasFrequency ? frequency : undefined,
        effectiveDate: hasFrequency ? effectiveDate : undefined,
        informationSource: hasInfoSource ? informationSource : undefined,
        active: hasActive ? activeChoice === 'active' : undefined,
        hasDirectAccess: hasAccess ? accessChoice === 'yes' : undefined,
        changedByName: activeAccount?.name ?? activeAccount?.username,
        changedById: activeAccount?.localAccountId,
      });
      setResult(res);
      onCommitted?.(res);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('bulkEdit.obligations.failed'));
    }
  }

  // ── Result phase ──────────────────────────────────────────────────────────
  if (result) {
    return (
      <>
        <DialogContent>
          <MessageBar intent={result.failed.length === 0 ? 'success' : 'warning'}>
            <MessageBarBody>
              {result.failed.length > 0
                ? t('bulkEdit.obligations.resultWithFailures', { count: result.succeeded.length, failed: result.failed.length })
                : t('bulkEdit.obligations.result', { count: result.succeeded.length })}
            </MessageBarBody>
          </MessageBar>
          {result.failed.length > 0 && (
            <div className={styles.list} style={{ marginTop: 8 }}>
              {result.failed.map((f) => (
                <div key={f.obligationId} className={styles.listRow}>
                  <span>{f.obligationId}</span>
                  <span className={styles.rowMeta}>{f.error}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button appearance="primary" onClick={() => onOpenChange(false)}>{t('bulkEdit.done')}</Button>
        </DialogActions>
      </>
    );
  }

  // ── Edit phase ────────────────────────────────────────────────────────────
  return (
    <>
      <DialogContent>
        {formError && (
          <MessageBar intent="error" style={{ marginBottom: 8 }}>
            <MessageBarBody>{formError}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.form}>
          <MessageBar intent="info">
            <MessageBarBody>
              {t('bulkEdit.obligations.intro', { count: flat.length })}
            </MessageBarBody>
          </MessageBar>

          {isLoading && <Spinner size="tiny" label={t('bulkEdit.obligations.loading')} />}
          {isError && (
            <MessageBar intent="error">
              <MessageBarBody>{t('bulkEdit.obligations.loadError')}</MessageBarBody>
            </MessageBar>
          )}

          {!isLoading && !isError && (
            <>
              <div className={styles.filterRow}>
                <Field label={t('bulkEdit.obligations.taxType')}>
                  <Dropdown
                    aria-label={t('bulkEdit.obligations.filterTaxType')}
                    value={filterType === ANY ? t('bulkEdit.any') : filterType}
                    selectedOptions={[filterType]}
                    onOptionSelect={(_e, d) => setFilterType(d.optionValue ?? ANY)}
                  >
                    <Option value={ANY}>{t('bulkEdit.any')}</Option>
                    {STATE_TAX_OBLIGATION_OPTIONS.taxType.map((tt) => (
                      <Option key={tt} value={tt}>{tt}</Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label={t('bulkEdit.obligations.state')}>
                  <Dropdown
                    aria-label={t('bulkEdit.obligations.filterState')}
                    value={filterState === ANY ? t('bulkEdit.any') : filterState}
                    selectedOptions={[filterState]}
                    onOptionSelect={(_e, d) => setFilterState(d.optionValue ?? ANY)}
                  >
                    <Option value={ANY}>{t('bulkEdit.any')}</Option>
                    {STATE_TAX_OBLIGATION_OPTIONS.state.map((s) => (
                      <Option key={s} value={s}>{s}</Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label={t('bulkEdit.obligations.status')}>
                  <Dropdown
                    aria-label={t('bulkEdit.obligations.filterStatus')}
                    value={filterStatus === 'any' ? t('bulkEdit.any') : filterStatus === 'active' ? t('bulkEdit.active') : t('bulkEdit.inactive')}
                    selectedOptions={[filterStatus]}
                    onOptionSelect={(_e, d) =>
                      setFilterStatus((d.optionValue as 'any' | 'active' | 'inactive') ?? 'active')
                    }
                  >
                    <Option value="any">{t('bulkEdit.any')}</Option>
                    <Option value="active">{t('bulkEdit.active')}</Option>
                    <Option value="inactive">{t('bulkEdit.inactive')}</Option>
                  </Dropdown>
                </Field>
              </div>

              <div className={styles.listHeader}>
                <span className={styles.countHint}>
                  {t('bulkEdit.obligations.selectedCount', { selected: selectedIds.size, total: filtered.length })}
                </span>
                <div className={styles.linkBtns}>
                  <Button
                    size="small"
                    appearance="transparent"
                    onClick={() => setSelectedIds(new Set(filtered.map((f) => f.obligation.id)))}
                  >
                    {t('bulkEdit.obligations.selectAll')}
                  </Button>
                  <Button size="small" appearance="transparent" onClick={() => setSelectedIds(new Set())}>
                    {t('bulkEdit.obligations.clear')}
                  </Button>
                </div>
              </div>

              <div className={styles.list}>
                {filtered.length === 0 && (
                  <Text className={styles.rowMeta}>{t('bulkEdit.obligations.noMatches')}</Text>
                )}
                {filtered.map((f) => {
                  const o = f.obligation;
                  return (
                    <label key={o.id} className={styles.listRow}>
                      <Checkbox
                        checked={selectedIds.has(o.id)}
                        onChange={(_e, d) => toggleId(o.id, !!d.checked)}
                      />
                      <span>{f.clientName}</span>
                      <span className={styles.rowMeta}>
                        · {o.taxType}
                        {o.stateLabel ? ` (${o.stateLabel})` : ''}
                        {o.filingFrequencyLabel ? ` · ${cadenceName(t, o.filingFrequencyLabel)}` : ''}
                        {!o.active ? ` · ${t('bulkEdit.obligations.rowInactive')}` : ''}
                      </span>
                    </label>
                  );
                })}
              </div>

              <span className={styles.sectionLabel}>{t('bulkEdit.obligations.setFields')}</span>
              <div className={styles.fieldRow}>
                <Field label={t('bulkEdit.filingFrequency')}>
                  <Dropdown
                    aria-label={t('bulkEdit.obligations.setFrequency')}
                    placeholder={t('bulkEdit.dontChange')}
                    value={hasFrequency ? frequency : t('bulkEdit.dontChange')}
                    selectedOptions={hasFrequency ? [frequency] : []}
                    onOptionSelect={(_e, d) => setFrequency(d.optionValue ?? UNCHANGED)}
                  >
                    <Option value={UNCHANGED}>{t('bulkEdit.dontChange')}</Option>
                    {STATE_TAX_OBLIGATION_OPTIONS.filingFrequency.map((fr) => (
                      <Option key={fr} value={fr}>{fr}</Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field
                  label={t('bulkEdit.effectiveDate')}
                  required={hasFrequency}
                  hint={hasFrequency ? t('bulkEdit.effectiveDateHint') : t('bulkEdit.effectiveDateRequiredHint')}
                >
                  <CalendarDateInput value={effectiveDate} disabled={!hasFrequency} onChange={setEffectiveDate} />
                </Field>
              </div>

              <Field label={t('bulkEdit.informationSource')}>
                <Dropdown
                  aria-label={t('bulkEdit.obligations.setInformationSource')}
                  placeholder={t('bulkEdit.dontChange')}
                  value={hasInfoSource ? informationSource : t('bulkEdit.dontChange')}
                  selectedOptions={hasInfoSource ? [informationSource] : []}
                  onOptionSelect={(_e, d) => setInformationSource(d.optionValue ?? UNCHANGED)}
                >
                  <Option value={UNCHANGED}>{t('bulkEdit.dontChange')}</Option>
                  {STATE_TAX_OBLIGATION_OPTIONS.informationSource.map((s) => (
                    <Option key={s} value={s}>{s}</Option>
                  ))}
                </Dropdown>
              </Field>

              <div className={styles.fieldRow}>
                <Field label={t('bulkEdit.obligations.activeStatus')}>
                  <Dropdown
                    aria-label={t('bulkEdit.obligations.setActiveStatus')}
                    value={activeChoice === 'unchanged' ? t('bulkEdit.dontChange') : activeChoice === 'active' ? t('bulkEdit.active') : t('bulkEdit.inactive')}
                    selectedOptions={[activeChoice]}
                    onOptionSelect={(_e, d) => setActiveChoice((d.optionValue as ActiveChoice) ?? 'unchanged')}
                  >
                    <Option value="unchanged">{t('bulkEdit.dontChange')}</Option>
                    <Option value="active">{t('bulkEdit.active')}</Option>
                    <Option value="inactive">{t('bulkEdit.inactive')}</Option>
                  </Dropdown>
                </Field>
                <Field label={t('bulkEdit.obligations.directAccess')}>
                  <Dropdown
                    aria-label={t('bulkEdit.obligations.setDirectAccess')}
                    value={accessChoice === 'unchanged' ? t('bulkEdit.dontChange') : accessChoice === 'yes' ? t('bulkEdit.yes') : t('bulkEdit.no')}
                    selectedOptions={[accessChoice]}
                    onOptionSelect={(_e, d) => setAccessChoice((d.optionValue as AccessChoice) ?? 'unchanged')}
                  >
                    <Option value="unchanged">{t('bulkEdit.dontChange')}</Option>
                    <Option value="yes">{t('bulkEdit.yes')}</Option>
                    <Option value="no">{t('bulkEdit.no')}</Option>
                  </Dropdown>
                </Field>
              </div>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button appearance="secondary" disabled={isBusy} onClick={() => onOpenChange(false)}>
          {t('bulkEdit.cancel')}
        </Button>
        <Button
          appearance="primary"
          disabled={isBusy || isLoading}
          icon={isBusy ? <Spinner size="tiny" /> : undefined}
          onClick={() => void apply()}
        >
          {t('bulkEdit.obligations.applyTo', { count: selectedIds.size })}
        </Button>
      </DialogActions>
    </>
  );
}
